import { useState, useEffect } from "react";
import BaseModal from "./BaseModal";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import DropDown from "@components/ui/dropdown/DropDown";
import Icon1 from "@assets/icons/Icon";
import {
  serviceboxProxy,
  type AgendaOption,
  type CreateRdvParams,
  type StepResult,
  type LogEntry,
  type TestConnectionResult,
} from "@services/serviceboxProxy";
import { showNotification } from "context/notification.service";
import { ordresApi } from "@services/api/ordres";
import { dossiersApi } from "@services/api/dossiers";
import { useOrdreStore } from "@store/ordreStore";
import type { Ordre } from "types/ordre";
import type { Dossier, DossierType } from "types/dossier";
import type { OrgServiceBox, OrgServiceBoxSite } from "types/org";
import { useDataStore } from "@store/user_org";

/** Map common marque strings to a normalised key for matching against sites */
function normaliseMarque(raw: string): string {
  return raw
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase()
    .trim();
}

function detectSite(
  sites: OrgServiceBoxSite[] | undefined,
  vehicleMarque: string | undefined,
): OrgServiceBoxSite | undefined {
  if (!sites?.length || !vehicleMarque) return undefined;
  const key = normaliseMarque(vehicleMarque);
  return sites.find((s) => normaliseMarque(s.marque) === key);
}

interface Props {
  ordre: Ordre;
  serviceboxConfig: OrgServiceBox;
  onClose: () => void;
}

function formatDateYYYYMMDD(ts?: { seconds: number }): string {
  if (!ts) return "";
  const d = new Date(ts.seconds * 1000);
  return d.getFullYear().toString() +
    (d.getMonth() + 1).toString().padStart(2, "0") +
    d.getDate().toString().padStart(2, "0");
}

function formatTimeHHMM(ts?: { seconds: number }): string {
  if (!ts) return "0900";
  const d = new Date(ts.seconds * 1000);
  return d.getHours().toString().padStart(2, "0") +
    d.getMinutes().toString().padStart(2, "0");
}

const STEP_ICON: Record<string, { color: string; icon: string }> = {
  ok: { color: "#059669", icon: "done2" },
  error: { color: "#DC2626", icon: "close" },
  skipped: { color: "#9CA3AF", icon: "minus" },
};

function StepLine({ step }: { step: StepResult }) {
  const style = STEP_ICON[step.status] || STEP_ICON.skipped;
  return (
    <div className="flex flex-row items-center gap-2 py-1">
      <Icon1 name={style.icon} size={14} color={style.color} />
      <span className="text-h3 font-medium" style={{ color: style.color }}>{step.name}</span>
      {step.detail && (
        <span className="text-h4 text-neutral-400 ml-auto">{step.detail}</span>
      )}
    </div>
  );
}

const DOSSIER_TYPE_LABELS: Record<string, string> = {
  particulier: "Particulier",
  professionnel: "Professionnel",
  loueur: "Loueur",
  garantie: "Garantie",
  occasion: "Véhicule occasion",
  interne: "Véhicule interne",
};

export default function ServiceBoxTransferModal({ ordre, serviceboxConfig, onClose }: Props) {
  const [receptionnaires, setReceptionnaires] = useState<AgendaOption[]>([]);
  const [equipes, setEquipes] = useState<AgendaOption[]>([]);
  const [selectedReceptionnaire, setSelectedReceptionnaire] = useState("");
  const [selectedEquipe, setSelectedEquipe] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [waitingForLock, setWaitingForLock] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [steps, setSteps] = useState<StepResult[]>([]);
  const [showLogs, setShowLogs] = useState(false);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [connectionTest, setConnectionTest] = useState<TestConnectionResult | null>(null);
  const [testing, setTesting] = useState(false);
  const [debugResults, setDebugResults] = useState<Array<{ test: string; result: string; detail: string }> | null>(null);
  const [debugging, setDebugging] = useState(false);
  const [searchingClient, setSearchingClient] = useState(false);
  const [clientSearchResult, setClientSearchResult] = useState<{ found: boolean; detail: string } | null>(null);
  const [parentDossier, setParentDossier] = useState<Dossier | null>(null);

  useEffect(() => {
    if (ordre.dossier_id) {
      dossiersApi.get(ordre.dossier_id).then(setParentDossier).catch(() => {});
    }
  }, [ordre.dossier_id]);

  const dossierType = parentDossier?.type as DossierType | undefined;

  const sites = serviceboxConfig.sites || [];
  const autoSite = detectSite(sites, ordre.vehicle?.marque);
  const peugeotFallback = !autoSite
    ? sites.find((s) => normaliseMarque(s.marque).includes("PEUG"))
    : undefined;
  const [selectedPdv, setSelectedPdv] = useState(
    autoSite?.pdv_id || peugeotFallback?.pdv_id || serviceboxConfig.site_code || "",
  );

  const activeSiteCode = selectedPdv || serviceboxConfig.site_code;
  const operatorEmail = useDataStore((s) => s.userData?.infos?.email) || "";

  const creds = {
    username: serviceboxConfig.username || "",
    password: serviceboxConfig.password || "",
    site_code: activeSiteCode,
    operator_email: operatorEmail,
  };

  const primaryContact = ordre.contacts?.find((c) => c.is_primary) || ordre.contacts?.[0];
  const vehicle = ordre.vehicle;

  useEffect(() => {
    serviceboxProxy
      .getOptions(creds)
      .then((opts) => {
        setReceptionnaires(opts.receptionnaires);
        setEquipes(opts.equipes);
        if (opts.receptionnaires.length > 0) setSelectedReceptionnaire(opts.receptionnaires[0].id);
        const mecanique = opts.equipes.find((e) => e.name.toUpperCase().includes("MECANIQUE"));
        setSelectedEquipe(mecanique ? mecanique.id : opts.equipes[0]?.id || "");
      })
      .catch((e) => setError("Impossible de contacter le proxy ServiceBox: " + e.message))
      .finally(() => setLoading(false));
  }, []);

  const handleTestConnection = async () => {
    setTesting(true);
    setConnectionTest(null);
    try {
      const result = await serviceboxProxy.testConnection(creds);
      setConnectionTest(result);
    } catch (e: any) {
      setConnectionTest({
        connected: false,
        session_ok: false,
        servicebox_reachable: false,
        detail: e.message,
      });
    } finally {
      setTesting(false);
    }
  };

  const handleDebugAuth = async () => {
    setDebugging(true);
    setDebugResults(null);
    try {
      const data = await serviceboxProxy.debugAuth(creds);
      setDebugResults(data.results);
    } catch (e: any) {
      setDebugResults([{ test: "Error", result: "error", detail: e.message }]);
    } finally {
      setDebugging(false);
    }
  };

  const handleSearchClient = async () => {
    setSearchingClient(true);
    setClientSearchResult(null);
    try {
      const phone = (primaryContact?.phone || "").replace(/\s/g, "");
      const nom = primaryContact?.nom || ordre.name || "";
      const result = await serviceboxProxy.searchClient(creds, phone, nom);
      setClientSearchResult(result);
    } catch (e: any) {
      setClientSearchResult({ found: false, detail: e.message });
    } finally {
      setSearchingClient(false);
    }
  };

  const handleFetchLogs = async () => {
    try {
      const entries = await serviceboxProxy.getLogs(30);
      setLogs(entries);
      setShowLogs(true);
    } catch {
      setLogs([]);
      setShowLogs(true);
    }
  };

  const handleTransfer = async () => {
    if (!selectedReceptionnaire || !selectedEquipe) {
      setError("Selectionnez un receptionnaire et une equipe");
      return;
    }
    setSending(true);
    setError(null);
    setSteps([]);

    try {
      const travaux = ordre.interventions.length > 0
        ? ordre.interventions.map((i) => ({
            nom: i.label || "Intervention",
            duree: (i.estimated_hours || 0.5).toFixed(2),
          }))
        : [{ nom: "Intervention", duree: "0.50" }];

      const civiliteMap: Record<string, string> = { "M.": "1", "Mme": "2", "Mlle": "3" };

      const params: CreateRdvParams = {
        ...creds,
        date: formatDateYYYYMMDD(ordre.reception_date),
        heure: formatTimeHHMM(ordre.reception_date),
        restitution_heure: formatTimeHHMM(ordre.restitution),
        receptionnaire_id: selectedReceptionnaire,
        equipe_id: selectedEquipe,
        nom: primaryContact?.nom || ordre.name || "",
        prenom: primaryContact?.prenom || "",
        tel_mobile: (primaryContact?.phone || "").replace(/\s/g, ""),
        email: primaryContact?.email || "",
        civilite_id: civiliteMap[primaryContact?.civilite || "M."] || "1",
        immatriculation: ordre.immatriculation || vehicle?.plate || "",
        vin: vehicle?.vin || "",
        marque_libelle: vehicle?.marque || "",
        ldp_libelle: vehicle?.modele || ordre.modele || "",
        kilometrage: vehicle?.km?.toString() || ordre.booking_request?.km || "",
        travaux,
        pdv_id: activeSiteCode || "011622H",
      };

      const result = await serviceboxProxy.createRdv(params, () => setWaitingForLock(true));
      setWaitingForLock(false);
      setSteps(result.steps || []);

      if (!result.success) {
        setError(result.error || "Erreur inconnue");
        setSending(false);
        return;
      }

      // Save OR number + rdv_id for potential revert
      // NOTE: result.dossier_id is the ServiceBox internal dossier ID, NOT the Firestore dossier_id.
      // Do NOT overwrite ordre.dossier_id with it — that breaks the link to contacts and vehicle.
      const updatePayload: Record<string, any> = {};
      if (result.or_number) updatePayload.or_number = result.or_number;
      if (result.or_number) updatePayload.num = `OR-${result.or_number}`;
      if (result.rdv_id) updatePayload.servicebox_rdv_id = result.rdv_id;
      if (result.dossier_id) updatePayload.servicebox_dossier_id = result.dossier_id;

      if (Object.keys(updatePayload).length > 0) {
        const updated = await ordresApi.update(ordre.id, updatePayload as any);
        useOrdreStore.getState().updateOrdre(updated);
      }

      if (result.or_number) {
        showNotification({ type: "success", message: `Transfert OK - OR n\u00B0${result.or_number}` });
      } else {
        showNotification({ type: "success", message: "RDV cree sur ServiceBox (pas de numero OR)" });
      }

      // Don't close — let user see the steps, they can close manually
    } catch (e: any) {
      setWaitingForLock(false);
      setError(e.message || "Erreur lors du transfert");
    } finally {
      setSending(false);
    }
  };

  const hasSucceeded = steps.length > 0 && steps.every((s) => s.status !== "error");

  return (
    <BaseModal
      onClose={onClose}
      title="Transfert ServiceBox"
      startIcon={<Icon1 name="sett-servicebox" size={20} />}
      width="max-w-xl"
    >
      <div className="flex flex-col gap-4 p-4">
        {/* Missing dossier_type gate */}
        {!dossierType && (
          <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-h3">
            <div className="font-medium mb-1">Type de dossier requis</div>
            <div>Veuillez définir le type de dossier (particulier, professionnel, loueur, etc.) dans le dossier avant de transférer vers ServiceBox.</div>
          </div>
        )}

        {/* Summary */}
        <div className="flex flex-col gap-2 p-3 rounded-lg bg-neutral-50 border border-neutral-200">
          <div className="text-h2 font-semibold text-neutral-700">Données à transférer</div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-h3">
            <span className="text-neutral-400">Type de dossier</span>
            <span className={`text-neutral-700 ${!dossierType ? "text-red-500 font-medium" : ""}`}>
              {dossierType ? DOSSIER_TYPE_LABELS[dossierType] || dossierType : "Non défini"}
            </span>
            <span className="text-neutral-400">Client</span>
            <span className="text-neutral-700">{primaryContact?.prenom || ""} {primaryContact?.nom || ordre.name}</span>
            <span className="text-neutral-400">Telephone</span>
            <span className="text-neutral-700">{primaryContact?.phone || "\u2014"}</span>
            <span className="text-neutral-400">Vehicule</span>
            <span className="text-neutral-700">{vehicle?.marque || ""} {vehicle?.modele || ordre.modele || ""}</span>
            <span className="text-neutral-400">Immatriculation</span>
            <span className="text-neutral-700">{ordre.immatriculation || vehicle?.plate || "\u2014"}</span>
            <span className="text-neutral-400">VIN</span>
            <span className="text-neutral-700">{vehicle?.vin || "\u2014"}</span>
            <span className="text-neutral-400">Interventions</span>
            <span className="text-neutral-700">
              {ordre.interventions.length > 0
                ? ordre.interventions.map((i) => i.label).join(", ")
                : "\u2014"}
            </span>
          </div>
        </div>

        {/* Options */}
        {loading ? (
          <div className="flex items-center justify-center py-6 text-neutral-400 text-h3">
            Chargement des options ServiceBox...
          </div>
        ) : !hasSucceeded ? (
          <div className="flex flex-col gap-3">
            {sites.length > 0 && (
              <DropDown
                label="Point de vente (marque)"
                value={selectedPdv}
                options={sites.map((s) => ({ value: s.pdv_id, label: s.label }))}
                onSelect={(v) => setSelectedPdv(v as string)}
              />
            )}
            <DropDown
              label="Réceptionnaire"
              value={selectedReceptionnaire}
              options={receptionnaires.map((r) => ({ value: r.id, label: r.name }))}
              onSelect={(v) => setSelectedReceptionnaire(v as string)}
            />
            <DropDown
              label="Équipe"
              value={selectedEquipe}
              options={equipes.map((e) => ({ value: e.id, label: e.name }))}
              onSelect={(v) => setSelectedEquipe(v as string)}
            />
          </div>
        ) : null}

        {/* Step-by-step progress */}
        {steps.length > 0 && (
          <div className="flex flex-col gap-0 p-3 rounded-lg border border-neutral-200 bg-white">
            <div className="text-h2 font-semibold text-neutral-700 mb-1">Progression</div>
            {steps.map((step, i) => (
              <StepLine key={i} step={step} />
            ))}
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-h3">
            {error}
          </div>
        )}

        {/* Connection test result */}
        {connectionTest && (
          <div
            className="p-3 rounded-lg border text-h3"
            style={{
              background: connectionTest.connected ? "#F0FDF4" : "#FEF2F2",
              borderColor: connectionTest.connected ? "#BBF7D0" : "#FECACA",
              color: connectionTest.connected ? "#166534" : "#991B1B",
            }}
          >
            <div className="font-medium mb-1">
              {connectionTest.connected ? "Connexion OK" : "Echec de connexion"}
            </div>
            <div className="text-h4">
              VPN/Reseau: {connectionTest.servicebox_reachable ? "OK" : "Echec"} |
              Session: {connectionTest.session_ok ? "OK" : "Echec"}
            </div>
            {connectionTest.detail && (
              <div className="text-h4 mt-1 opacity-80">{connectionTest.detail}</div>
            )}
          </div>
        )}

        {/* Client search result */}
        {clientSearchResult && (
          <div
            className="p-3 rounded-lg border text-h3"
            style={{
              background: clientSearchResult.found ? "#F0FDF4" : "#FEF2F2",
              borderColor: clientSearchResult.found ? "#BBF7D0" : "#FECACA",
              color: clientSearchResult.found ? "#166534" : "#991B1B",
            }}
          >
            <div className="font-medium mb-1">
              {clientSearchResult.found ? "Client trouve dans le DMS" : "Client non trouve"}
            </div>
            <div className="text-h4">{clientSearchResult.detail}</div>
          </div>
        )}

        {/* Logs panel */}
        {showLogs && (
          <div className="flex flex-col border border-neutral-200 rounded-lg overflow-hidden">
            <div className="flex flex-row items-center justify-between px-3 py-2 bg-neutral-50 border-b border-neutral-200">
              <span className="text-h3 font-medium text-neutral-600">Logs du proxy</span>
              <div className="flex flex-row items-center gap-2">
                <button
                  onClick={() => {
                    const text = logs.map((l) => `${l.ts.split("T")[1]?.slice(0, 8)} [${l.step}] ${l.message}`).join("\n");
                    navigator.clipboard.writeText(text);
                    showNotification({ type: "success", message: "Logs copies" });
                  }}
                  className="text-h4 text-blue-500 hover:text-blue-700"
                >
                  Copier
                </button>
                <button onClick={() => setShowLogs(false)} className="text-h4 text-neutral-400 hover:text-neutral-600">
                  Fermer
                </button>
              </div>
            </div>
            <div className="max-h-[200px] overflow-y-auto p-2 bg-neutral-900 font-mono text-[11px] leading-[16px]">
              {logs.length === 0 ? (
                <div className="text-neutral-500">Aucun log</div>
              ) : (
                logs.map((log, i) => {
                  const color = log.level === "error" ? "#F87171" : log.level === "warn" ? "#FBBF24" : "#9CA3AF";
                  return (
                    <div key={i} style={{ color }}>
                      <span className="text-neutral-600">{log.ts.split("T")[1]?.slice(0, 8)}</span>
                      {" "}
                      {log.step && <span className="text-blue-400">[{log.step}]</span>}
                      {" "}{log.message}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* Debug results */}
        {debugResults && (
          <div className="flex flex-col border border-neutral-200 rounded-lg overflow-hidden">
            <div className="flex flex-row items-center justify-between px-3 py-2 bg-neutral-50 border-b border-neutral-200">
              <span className="text-h3 font-medium text-neutral-600">Debug Auth</span>
              <button onClick={() => setDebugResults(null)} className="text-h4 text-neutral-400 hover:text-neutral-600">
                Fermer
              </button>
            </div>
            <div className="max-h-[300px] overflow-y-auto p-2 bg-neutral-900 font-mono text-[11px] leading-[16px]">
              {debugResults.map((r, i) => {
                const color = r.result === "ok" || r.result.includes("200") ? "#34D399" : r.result === "error" || r.result.includes("401") ? "#F87171" : "#9CA3AF";
                return (
                  <div key={i} className="py-0.5">
                    <span style={{ color }} className="font-bold">[{r.result}]</span>
                    {" "}
                    <span className="text-blue-300">{r.test}</span>
                    {" "}
                    <span className="text-neutral-400 break-all">{r.detail}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Diagnostic links */}
        <div className="flex flex-row items-center gap-3 text-[11px]">
          <button
            onClick={handleTestConnection}
            disabled={testing}
            className="text-neutral-400 hover:text-neutral-600 cursor-pointer disabled:opacity-50"
          >
            {testing ? "Test..." : "Tester connexion"}
          </button>
          <button
            onClick={handleSearchClient}
            disabled={searchingClient}
            className="text-neutral-400 hover:text-neutral-600 cursor-pointer disabled:opacity-50"
          >
            {searchingClient ? "Recherche..." : "Chercher client DMS"}
          </button>
          <button
            onClick={handleDebugAuth}
            disabled={debugging}
            className="text-neutral-400 hover:text-neutral-600 cursor-pointer disabled:opacity-50"
          >
            {debugging ? "Debug..." : "Debug auth"}
          </button>
          <button
            onClick={handleFetchLogs}
            className="text-neutral-400 hover:text-neutral-600 cursor-pointer"
          >
            Logs
          </button>
        </div>

        {/* Actions */}
        <div className="flex flex-row items-center justify-end gap-2 pt-1">
          {hasSucceeded ? (
            <Button label="Fermer" onClick={onClose} />
          ) : (
            <>
              <SecondaryButton label="Annuler" onClick={onClose} />
              <Button
                label={waitingForLock ? "En attente d'un autre utilisateur..." : sending ? "Transfert en cours..." : "Transferer"}
                onClick={handleTransfer}
                disabled={loading || sending || !selectedReceptionnaire || !selectedEquipe || !dossierType}
                loadingBar={sending}
              />
            </>
          )}
        </div>
      </div>
    </BaseModal>
  );
}
